import React, { useMemo } from "react";
import { useApp } from "@/contexts/AppContext";
import { safeFetch } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import SeverityBar from "@/components/shared/SeverityBar";
import HierarchyCrumb from "@/components/shared/HierarchyCrumb";
import { LineChart, Line, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { TrendingUp, TrendingDown, Zap, Radar, Users, ChevronRight, ChevronDown } from "lucide-react";
// No mockData import

const SEV_COLOR = { critical: "var(--sev-critical)", high: "var(--sev-high)", medium: "var(--sev-medium)", low: "var(--sev-low)" };

// O(n) coordination score — replaces the O(n²) nested loop that caused 2,440ms INP
const CoordinationScore = React.memo(function CoordinationScore({ filtered }) {
  const { pct, color, label } = useMemo(() => {
    if (!filtered.length) return { pct: 0, color: "var(--sev-low)", label: "organic" };
    const DAY = 86400 * 1000;
    // Bucket posts by subcategory+day for O(n) clustering
    const buckets = {};
    const len = filtered.length;
    for (let i = 0; i < len; i++) {
      const p = filtered[i];
      const day = Math.floor(new Date(p.created_at || p.timestamp).getTime() / DAY);
      const key = `${p.subcategory || ""}__${day}`;
      buckets[key] = (buckets[key] || 0) + 1;
    }
    // Count posts that share a bucket with at least one other post
    let clustered = 0;
    for (let i = 0; i < len; i++) {
      const p = filtered[i];
      const day = Math.floor(new Date(p.created_at || p.timestamp).getTime() / DAY);
      const key = `${p.subcategory || ""}__${day}`;
      if (buckets[key] > 1) clustered++;
    }
    const pct = Math.round((clustered / len) * 100);
    const color = pct >= 60 ? "var(--sev-critical)" : pct >= 30 ? "var(--sev-high)" : "var(--sev-low)";
    const label = pct >= 60 ? "likely campaign" : pct >= 30 ? "mixed" : "organic";
    return { pct, color, label };
  }, [filtered]);

  return (
    <>
      <div className="font-display text-sm" style={{ color }}>{pct}%</div>
      <div className="label-micro">{label}</div>
    </>
  );
});

function bandColor(r) {
  if (r >= 0.75) return "var(--sev-critical)";
  if (r >= 0.5) return "var(--sev-high)";
  if (r >= 0.25) return "var(--sev-medium)";
  return "var(--sev-low)";
}

function MiniDial({ value, color }) {
  const cx = 36, cy = 36, r = 28;
  const polar = (d) => { const a = (d * Math.PI) / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; };
  const arc = (f, t) => { const [x1, y1] = polar(f); const [x2, y2] = polar(t); return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`; };
  const ang = 180 + value * 180;
  const rad = (ang * Math.PI) / 180;
  const nx = cx + (r - 4) * Math.cos(rad);
  const ny = cy + (r - 4) * Math.sin(rad);
  return (
    <svg viewBox="0 0 72 44" className="w-[64px] h-[40px]">
      <path d={arc(180, 360)} stroke="rgba(0,0,0,0.08)" strokeWidth="6" fill="none" />
      <path d={arc(180, 180 + value * 180)} stroke={color} strokeWidth="6" fill="none" />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="var(--text-primary)" strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r="2" fill="var(--text-primary)" />
    </svg>
  );
}

/* Severity tree: ven→site→subcategory rows with risk bars */
const SeverityTree = React.memo(function SeverityTree({ severity }) {
  const { setSelectedPost, posts: allPosts } = useApp();
  const posts = useMemo(() => allPosts || [], [allPosts]);

  const tree = useMemo(() => {
    const t = {};
    const len = posts.length;
    for (let i = 0; i < len; i++) {
      const p = posts[i];
      const vName = (p.venture || "Adani Group").trim();
      if (!t[vName]) t[vName] = { name: vName, signals: 0, risk: 0, sites: {} };
      t[vName].signals++; t[vName].risk += p.risk_score;
      const siteKey = p.site || "Unknown";
      if (!t[vName].sites[siteKey]) t[vName].sites[siteKey] = { name: siteKey, state: p.state, signals: 0, risk: 0, subs: {} };
      const st = t[vName].sites[siteKey];
      st.signals++; st.risk += p.risk_score;
      const subKey = p.subcategory || "General";
      if (!st.subs[subKey]) st.subs[subKey] = { name: subKey, category: p.category, signals: 0, risk: 0, examples: [] };
      const sub = st.subs[subKey];
      sub.signals++; sub.risk += p.risk_score;
      if (sub.examples.length < 3) sub.examples.push(p);
    }
    return Object.values(t).map((v) => ({
      ...v, avg_risk: (v.risk / Math.max(1, v.signals)) / 10,
      sites: Object.values(v.sites).map((s) => ({
        ...s, avg_risk: (s.risk / Math.max(1, s.signals)) / 10,
        subs: Object.values(s.subs).map((sb) => ({ ...sb, avg_risk: (sb.risk / Math.max(1, sb.signals)) / 10 })).sort((a, b) => b.avg_risk - a.avg_risk),
      })).sort((a, b) => b.avg_risk - a.avg_risk),
    })).sort((a, b) => b.avg_risk - a.avg_risk);
  }, [posts]);

  const [openV, setOpenV] = React.useState(() => new Set(tree.slice(0, 1).map((x) => x.name)));
  const [openS, setOpenS] = React.useState(new Set());
  const toggle = (set, key) => {
    const n = new Set(set); n.has(key) ? n.delete(key) : n.add(key); return n;
  };

  return (
    <div data-testid={`severity-tree-${severity}`} className="panel h-full overflow-y-auto border-0">
      {tree.map((v) => {
        const vOpen = openV.has(v.name);
        return (
          <div key={v.name} className="border-b border-hair">
            <button
              type="button"
              onClick={() => setOpenV(toggle(openV, v.name))}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-black/[0.03]"
            >
              {vOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <span className="font-display text-sm flex-1 text-left">{v.name}</span>
              <span className="flex-1 max-w-[160px] h-1.5 bg-black/5 hidden md:block">
                <span className="block h-full" style={{ background: bandColor(v.avg_risk), width: `${Math.min(100, v.avg_risk * 100)}%` }} />
              </span>
              <span className="label-micro w-10 text-right" style={{ color: bandColor(v.avg_risk) }}>{(v.avg_risk * 100).toFixed(0)}</span>
              <span className="label-micro w-12 text-right">{v.signals}</span>
            </button>

            {vOpen && v.sites.map((s) => {
              const sKey = `${v.name}__${s.name}`;
              const sOpen = openS.has(sKey);
              return (
                <div key={sKey}>
                  <button
                    type="button"
                    onClick={() => setOpenS(toggle(openS, sKey))}
                    className="w-full flex items-center gap-2 px-3 py-1.5 pl-8 hover:bg-black/[0.03] border-t border-hair"
                  >
                    {sOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="text-xs flex-1 text-left">{s.name} <span className="label-micro ml-1.5">{s.state}</span></span>
                    <span className="flex-1 max-w-[160px] h-1 bg-black/5 hidden md:block">
                      <span className="block h-full" style={{ background: bandColor(s.avg_risk), width: `${Math.min(100, s.avg_risk * 100)}%` }} />
                    </span>
                    <span className="label-micro w-10 text-right">{(s.avg_risk * 100).toFixed(0)}</span>
                    <span className="label-micro w-12 text-right">{s.signals}</span>
                  </button>
                  {sOpen && s.subs.map((sub) => (
                    <div key={sub.name} className="pl-14 pr-3 py-1.5 border-t border-hair hover:bg-black/[0.03]">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5" style={{ background: SEV_COLOR[severity] }} />
                        <span className="text-xs flex-1 truncate">{sub.name}</span>
                        <span className="label-micro">{sub.category}</span>
                        <span className="label-micro w-10 text-right" style={{ color: bandColor(sub.avg_risk) }}>{(sub.avg_risk * 100).toFixed(0)}</span>
                        <span className="label-micro w-8 text-right">{sub.signals}</span>
                      </div>
                      <div className="mt-1 pl-3 space-y-0.5">
                        {sub.examples.map((ex) => (
                          <button key={ex.id} onClick={() => setSelectedPost(ex)} className="block text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-left w-full truncate">
                            · {ex.content}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      {!tree.length && <div className="p-4 label-micro">No {severity} signals.</div>}
    </div>
  );
});

export default function DetailDrawer() {
  const { 
    selectedDetail, setSelectedDetail, posts, setSelectedPost,
    categoryDist, ventureDist, topSites, locations, kpis, filters 
  } = useApp();
  
  const [drawerPosts, setDrawerPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const open = !!selectedDetail;
  const type = selectedDetail?.type;

  // Real-time reactive data lookup: ensures drawer stays synced with WS updates
  const d = useMemo(() => {
    const base = selectedDetail?.payload || {};
    if (!open) return {};

    if (type === "category") {
      return categoryDist.find(c => c.category === base.category) || base;
    }
    if (type === "venture") {
      const live = ventureDist.find(v => v.category === base.name || v.venture === base.name);
      return live ? { ...live, name: live.category || live.venture } : base;
    }
    if (type === "site") {
      return topSites.find(s => s.site === base.name) || base;
    }
    if (type === "state") {
      return locations.states?.find(s => s.state === base.state) || base;
    }
    if (type === "severity") {
      const s = base.severity?.toLowerCase();
      if (s === 'critical') return { ...base, signals: kpis?.critical_count || base.count, critical: kpis?.critical_count || base.count };
      if (s === 'high') return { ...base, signals: kpis?.high_risk_count || base.count };
      return base;
    }
    return base;
  }, [selectedDetail, open, type, categoryDist, ventureDist, topSites, locations, kpis]);

  const title = d.name || d.category || d.site || d.venture || d.state || (d.severity ? `${d.severity.toUpperCase()} Signals` : "Detail");

  const filtered = useMemo(() => {
    if (!selectedDetail) return [];
    if (drawerPosts && drawerPosts.length > 0) return drawerPosts;
    
    const source = posts || [];
    return source.filter((p) => {
      if (type === "severity") return p.severity === d.severity;
      if (type === "venture") return p.venture === d.name;
      if (type === "site") return p.site === d.name || (!p.site && p.platform === d.name) || (!p.site && !p.platform && d.name === "OSINT Signal");
      if (type === "category") return p.category === d.category;
      if (type === "category-site") {
        const matchSite = p.site === d.site || (!p.site && p.platform === d.site) || (!p.site && !p.platform && d.site === "OSINT Signal");
        return matchSite && p.category === d.name;
      }
      if (type === "subpoint") return p.subcategory === d.name;
      if (type === "state") return p.state === d.state;
      return true;
    });
  }, [selectedDetail, posts, drawerPosts, d, type]);

  React.useEffect(() => {
    if (!open) {
      setDrawerPosts([]);
      return;
    }

    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = { ...filters, limit: 200 };
        if (type === "site") params.site = d.name || d.site;
        if (type === "category") params.category = d.category;
        if (type === "venture") params.venture = d.name || d.venture;
        if (type === "state") params.state = d.state;
        if (type === "severity") params.severity = d.severity;

        // Sanitize params to avoid sending "null" or "undefined" as strings
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === "") {
            delete params[key];
          }
        });

        let res = await safeFetch("/posts", { params });
        
        // Failsafe: if filtered result is empty but we know signals exist, retry with broad filters
        if ((!res.data?.items || res.data.items.length === 0) && (d.signals > 0)) {
          const broadParams = { limit: 200, window: params.window || "30d" };
          if (type === "site") broadParams.site = params.site;
          if (type === "state") broadParams.state = params.state;
          if (type === "venture") broadParams.venture = params.venture;
          
          const retry = await safeFetch("/posts", { params: broadParams });
          if (retry.data?.items && retry.data.items.length > 0) {
            res = retry;
          }
        }

        if (res.data?.items) {
          setDrawerPosts(res.data.items);
        }
      } catch (err) {
        console.error("Failed to fetch detail posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [open, type, d.name, d.site, d.category, d.venture, d.state, d.severity, d.signals, filters]);

  const stats = useMemo(() => {
    // Priority: Use pre-aggregated numbers from the payload (passed from the card) to ensure UI consistency
    // Fallback: Calculate from filtered local posts if payload metrics are missing
    const signals = d.signals ?? filtered.length;
    const critical = d.critical ?? filtered.filter((p) => p.severity === "critical").length;
    const avgRisk = d.avg_risk ?? (filtered.length ? filtered.reduce((a, p) => a + p.risk_score, 0) / filtered.length : 0);
    
    // For sentiment/quadrant stats that aren't always in payload, calculate from available sample
    const avgSent = filtered.length ? filtered.reduce((a, p) => a + p.sentiment, 0) / filtered.length : 0;
    const pos = filtered.filter((p) => p.sentiment > 0.15).length;
    const neg = filtered.filter((p) => p.sentiment < -0.15).length;
    const neu = filtered.length - pos - neg;
    
    return { signals, critical, avgRisk, avgSent, pos, neu, neg };
  }, [filtered, d]);

  const severity = useMemo(() => {
    const m = { critical: 0, high: 0, medium: 0, low: 0 };
    filtered.forEach((p) => { m[p.severity]++; });
    
    // Total signals we MUST reach
    const targetTotal = stats.signals;
    const targetCritical = stats.critical;
    
    // 1. Critical is fixed
    m.critical = targetCritical;
    
    // 2. We need to distribute the remaining signals among high, medium, low
    const targetRemaining = targetTotal - targetCritical;
    const sampleRemaining = Math.max(1, (filtered.length - filtered.filter(p => p.severity === 'critical').length));
    
    if (targetRemaining > 0) {
      // Scale based on sample distribution
      m.high = Math.round((m.high / sampleRemaining) * targetRemaining);
      m.medium = Math.round((m.medium / sampleRemaining) * targetRemaining);
      // Low gets the rest to ensure exact sum
      m.low = Math.max(0, targetRemaining - m.high - m.medium);
    } else {
      m.high = 0; m.medium = 0; m.low = 0;
    }
    
    const t = targetTotal || 1;
    return ["critical", "high", "medium", "low"].map((s) => ({ 
      severity: s, 
      count: m[s], 
      percent: Math.round((m[s] / t) * 100) 
    }));
  }, [filtered, stats.signals, stats.critical]);

  const topNarratives = useMemo(() => {
    const m = {};
    filtered.forEach((p) => {
      if (!m[p.subcategory]) m[p.subcategory] = { key: p.subcategory, count: 0, critical: 0, risk: 0 };
      m[p.subcategory].count++;
      if (p.severity === "critical") m[p.subcategory].critical++;
      m[p.subcategory].risk += p.risk_score;
    });
    return Object.values(m).map((x) => ({ ...x, risk: x.risk / Math.max(1, x.count) }))
      .sort((a, b) => b.critical * 2 + b.risk - (a.critical * 2 + a.risk))
      .slice(0, 8);
  }, [filtered]);

  const topAuthors = useMemo(() => {
    const m = {};
    filtered.forEach((p) => {
      if (!m[p.handle]) m[p.handle] = { handle: p.handle, name: p.author, posts: 0, critical: 0, reach: 0 };
      m[p.handle].posts++;
      if (p.severity === "critical") m[p.handle].critical++;
      m[p.handle].reach += p.reach || 0;
    });
    return Object.values(m).sort((a, b) => b.critical - a.critical || b.reach - a.reach).slice(0, 25);
  }, [filtered]);

    const velocity = useMemo(() => {
      const out = [], DAY = 86400 * 1000;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfToday = today.getTime();

      for (let i = 6; i >= 0; i--) {
        const start = startOfToday - i * DAY, end = start + DAY;
        const count = filtered.filter((p) => { 
          const t = new Date(p.created_at || p.timestamp).getTime(); 
          return t >= start && t < end; 
        }).length;
        out.push({ d: `D-${i}`, count });
      }
      const recent = out.slice(-3).reduce((a, x) => a + x.count, 0);
      const prior = out.slice(0, 3).reduce((a, x) => a + x.count, 0);
      const delta = prior > 0 ? ((recent - prior) / prior) * 100 : (recent > 0 ? 100 : 0);
      return { series: out, delta };
    }, [filtered]);

  const platformAmp = useMemo(() => {
    const m = {};
    filtered.forEach((p) => {
      if (!m[p.platform]) m[p.platform] = { platform: p.platform, total: 0, critical: 0 };
      m[p.platform].total++;
      if (p.severity === "critical") m[p.platform].critical++;
    });
    return Object.values(m).sort((a, b) => b.critical - a.critical);
  }, [filtered]);

  const statesInvolved = useMemo(() => {
    const m = {};
    filtered.forEach((p) => {
      if (!m[p.state]) m[p.state] = { state: p.state, count: 0, critical: 0 };
      m[p.state].count++;
      if (p.severity === "critical") m[p.state].critical++;
    });
    return Object.values(m).sort((a, b) => b.critical - a.critical).slice(0, 8);
  }, [filtered]);

  const sentTotal = Math.max(1, stats.pos + stats.neu + stats.neg);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && setSelectedDetail(null)}>
      <SheetContent
        side="right"
        data-testid="detail-drawer"
        aria-describedby="detail-drawer-description"
        className="w-screen sm:max-w-none h-screen max-h-screen rounded-none bg-[var(--bg-app)] border-l border-hair text-[var(--text-primary)] p-0 overflow-hidden z-[90] flex flex-col"
      >
        <SheetDescription className="sr-only">
          Detailed analytical breakdown of intelligence signals for {title}.
        </SheetDescription>
        <SheetHeader className="px-3 py-2 border-b border-hair bg-[var(--bg-surface)] text-left flex-shrink-0 space-y-0">
          <div className="flex items-center justify-between">
            <div className="label-micro">{(type || "detail").toUpperCase()}</div>
            <div className="flex items-center gap-2 label-micro">
              {velocity.delta > 0
                ? <span className="flex items-center gap-1 text-[var(--sev-critical)]"><TrendingUp className="w-3 h-3" /> +{Math.round(velocity.delta)}%</span>
                : velocity.delta < 0 
                ? <span className="flex items-center gap-1 text-[var(--sev-low)]"><TrendingDown className="w-3 h-3" /> {Math.round(velocity.delta)}%</span>
                : <span className="flex items-center gap-1 text-[var(--text-muted)]">Stable</span>
              }
              <span>· 7D Volume Trend</span>
            </div>
          </div>
          <SheetTitle className="text-[var(--text-primary)] font-display text-base lg:text-lg tracking-tight mt-0.5">{title}</SheetTitle>
          <HierarchyCrumb
            venture={d.venture || (type === "venture" ? d.name : null)}
            site={d.site || (type === "site" ? d.name : null)}
            category={type === "category" ? d.category : d.category}
            subcategory={type === "subpoint" ? d.name : null}
            severity={type === "severity" ? d.severity : null}
            state={d.state}
            platform={null}
          />
          <div className="grid grid-cols-4 gap-px bg-[var(--border-default)] mt-2">
            <div className="bg-[var(--bg-surface)] px-2 py-1"><div className="label-micro">Signals</div><div className="font-display text-sm">{stats.signals}</div></div>
            <div className="bg-[var(--bg-surface)] px-2 py-1"><div className="label-micro">Critical</div><div className="font-display text-sm sev-critical">{stats.critical}</div></div>
            <div className="bg-[var(--bg-surface)] px-2 py-1"><div className="label-micro">Avg Risk</div><div className="font-display text-sm sev-high">{stats.avgRisk.toFixed(2)}</div></div>
            <div className="bg-[var(--bg-surface)] px-2 py-1"><div className="label-micro">Avg Sentiment</div>
              <div className="font-display text-sm" style={{ color: stats.avgSent < 0 ? "var(--sev-high)" : "var(--sev-low)" }}>{stats.avgSent.toFixed(2)}</div>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden p-3">
         <div className="lg:h-full grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-12 lg:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">



          {/* Row 1 — Executive Brief & SeverityBar */}
          <section className="panel md:col-span-2 lg:col-span-12 lg:row-start-1 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0 text-[var(--text-secondary)]">Executive Brief · At-a-glance</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] flex-shrink-0">
              <div className="bg-[var(--bg-surface)] px-2 py-1.5 col-span-2">
                <div className="label-micro text-[var(--text-muted)]">Volume Density</div>
                <div className="font-display text-sm">{stats.signals} signals</div>
                <div className="label-micro text-[var(--text-muted)]">total in selected window</div>
              </div>
              <div className="bg-[var(--bg-surface)] px-2 py-1.5 col-span-2 border-l border-hair">
                <div className="label-micro text-[var(--text-muted)]">Avg Risk Score</div>
                <div className="font-display text-sm sev-high">{stats.avgRisk.toFixed(2)}</div>
                <div className="label-micro text-[var(--text-muted)]">avg threat magnitude (0-10)</div>
              </div>
            </div>
            <div className="p-3 border-t border-hair bg-black/[0.01]">
              <SeverityBar data={severity} />
            </div>
          </section>

          {/* Row 2 — Stakeholder Tiers */}
          <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0">Stakeholder Tiers · by follower influence</div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3">
              {(() => {
                const tiers = { "Celebrity / Major Media": 0, "Influencer / Mid-Media": 0, "Activist / Niche": 0, "Grassroots": 0 };
                const colors = { "Celebrity / Major Media": "var(--sev-critical)", "Influencer / Mid-Media": "var(--sev-high)", "Activist / Niche": "var(--sev-medium)", "Grassroots": "var(--sev-low)" };
                filtered.forEach((x) => {
                  const f = x.author_followers || 0;
                  if (f >= 1e6) tiers["Celebrity / Major Media"]++;
                  else if (f >= 1e5) tiers["Influencer / Mid-Media"]++;
                  else if (f >= 1e4) tiers["Activist / Niche"]++;
                  else tiers["Grassroots"]++;
                });
                const max = Math.max(1, ...Object.values(tiers));
                return (
                  <div className="space-y-1.5">
                    {Object.entries(tiers).map(([t, n]) => (
                      <div key={t} className="flex items-center gap-2 text-[11px]">
                        <span className="whitespace-nowrap flex-shrink-0">{t}</span>
                        <div className="flex-1 min-w-[24px] h-1.5 bg-black/5">
                          <div className="h-full" style={{ background: colors[t], width: `${(n / max) * 100}%` }} />
                        </div>
                        <span className="font-display w-8 text-right flex-shrink-0">{n}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                {(() => {
                  const legal = filtered.some((x) => x.category === "Legal & Litigation");
                  const fin = filtered.some((x) => x.category === "Financial Irregularities");
                  const needsBoard = stats.critical >= 10 || (stats.signals > 0 && stats.critical / Math.max(1, stats.signals) > 0.4);
                  const chips = [];
                  if (legal) chips.push({ t: "LEGAL PROXIMITY", c: "var(--sev-critical)" });
                  if (fin) chips.push({ t: "FINANCIAL EXPOSURE", c: "var(--sev-critical)" });
                  if (needsBoard) chips.push({ t: "BOARD-ESCALATION LIKELY", c: "var(--sev-high)" });
                  if (!chips.length) chips.push({ t: "NO IMMEDIATE ESCALATION", c: "var(--sev-low)" });
                  return chips.map((c) => (
                    <span key={c.t} className="label-micro px-1.5 py-0.5 border" style={{ color: c.c, borderColor: c.c }}>{c.t}</span>
                  ));
                })()}
              </div>
            </div>
          </section>

          {/* Row 2 — Verbatim Quotes */}
          <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0">Verbatim Signal Quotes · Top-amplified</div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
              {[...filtered].sort((a, b) => (b.shares || 0) - (a.shares || 0)).slice(0, 5).map((x) => (
                <button
                  key={x.id}
                  type="button"
                  onClick={() => setSelectedPost(x)}
                  className="w-full text-left border-l-2 pl-2.5 py-1 hover:bg-black/[0.03]"
                  style={{ borderColor: SEV_COLOR[x.severity] }}
                >
                  <div className="text-[11px] italic leading-snug text-[var(--text-primary)]">"{(x.content || "").slice(0, 170)}"</div>
                  <div className="label-micro mt-1 truncate">— {x.author} · {x.handle} · {((x.shares || 0) / 1000).toFixed(1)}K shares · {x.platform}</div>
                </button>
              ))}
              {!filtered.length && <div className="label-micro">No quotes available.</div>}
            </div>
          </section>

          {/* Row 2 — Narrative Themes (Expanded to fill space) */}
          <section className="panel lg:col-span-6 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0 flex items-center gap-1.5"><Radar className="w-3 h-3" /> Narrative Themes</div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
              {topNarratives.map((n) => (
                <div key={n.key} className="flex items-center gap-3">
                  <div className="text-[12px] flex-1 min-w-0 truncate font-medium">{n.key}</div>
                  <div className="w-32 h-2 bg-black/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--sev-critical)]" style={{ width: `${Math.min(100, (n.count / Math.max(1, filtered.length)) * 100 * 3)}%` }} />
                  </div>
                  <div className="label-micro w-16 text-right font-display">{n.critical} critical / {n.count} total</div>
                </div>
              ))}
              {!topNarratives.length && <div className="label-micro">No narrative themes identified.</div>}
            </div>
          </section>

          {/* Row 3 — Top Amplifiers + Platform */}
          <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0 flex items-center gap-1.5"><Users className="w-3 h-3" /> Top Amplifiers</div>
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pt-3 space-y-1.5">
              {topAuthors.map((a) => (
                <div key={a.handle} className="flex items-center gap-2 text-[11px]">
                  <div className="flex-1 min-w-0 truncate">
                    <span className="text-[var(--text-primary)]">{a.name}</span>
                    <span className="text-[var(--text-muted)] ml-1.5">{a.handle}</span>
                  </div>
                  <span className="label-micro">{Math.round(a.reach / 1000)}K</span>
                  <span className="font-display sev-critical w-6 text-right">{a.critical}</span>
                  <span className="font-display w-8 text-right">{a.posts}</span>
                </div>
              ))}
              {!topAuthors.length && <div className="label-micro">No amplifiers.</div>}
            </div>
            <div className="flex-shrink-0 border-t border-hair p-3">
              <div className="label-micro mb-2">Platform Amplification</div>
              <div className="space-y-1">
                {platformAmp.map((p) => (
                  <div key={p.platform} className="flex items-center gap-2 text-[11px]">
                    <span className="w-20 truncate capitalize">{p.platform}</span>
                    <div className="flex-1 h-1.5 bg-black/5"><div className="h-full bg-[var(--sev-high)]" style={{ width: `${Math.min(100, (p.critical / Math.max(1, stats.critical || p.total)) * 100)}%` }} /></div>
                    <span className="font-display w-10 text-right">{p.critical}/{p.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Row 3 — Top Critical Signals */}
          <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0">Top Critical Signals</div>
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-[var(--border-default)]">
              {[...filtered].sort((a, b) => b.risk_score - a.risk_score).slice(0, 12).map((p) => (
                <button
                  key={p.id}
                  data-testid={`critical-signal-${p.id}`}
                  onClick={() => setSelectedPost(p)}
                  className="w-full text-left py-2 px-3 hover:bg-black/[0.03]"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5" style={{ background: SEV_COLOR[p.severity] }} />
                    <span className="label-micro">{p.platform} · {p.state}</span>
                    <span className="font-display text-[10px] ml-auto sev-critical">{p.risk_score}</span>
                  </div>
                  <div className="text-xs mt-1 line-clamp-2">{p.content}</div>
                </button>
              ))}
              {!filtered.length && <div className="label-micro p-3">No critical signals.</div>}
            </div>
          </section>

          {/* Row 3 — Geographic Spread */}
          <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0">Geographic Spread</div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1.5">
              {statesInvolved.map((s) => (
                <div key={s.state} className="flex items-center gap-2 text-[11px]">
                  <span className="w-28 truncate">{s.state}</span>
                  <div className="flex-1 h-1.5 bg-black/5"><div className="h-full bg-[var(--sev-critical)]" style={{ width: `${Math.min(100, (s.critical / Math.max(1, stats.critical)) * 100)}%` }} /></div>
                  <span className="font-display w-10 text-right">{s.critical}/{s.count}</span>
                </div>
              ))}
              {!statesInvolved.length && <div className="label-micro">No geographic data.</div>}
            </div>
          </section>

          {/* Row 3 — Conditional: Severity tree OR Sub-category dials (fills 8th tile) */}
          {type === "severity" && (
            <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
              <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0">Signal Tree · VEN → SITE → SUB</div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <SeverityTree severity={d.severity} />
              </div>
            </section>
          )}
          {type === "category" && Array.isArray(d.subcategories) && d.subcategories.length > 0 && (
            <section className="panel lg:col-span-3 min-h-0 flex flex-col overflow-hidden">
              <div className="px-3 py-2 border-b border-hair label-micro flex-shrink-0">Sub-category Risk · {d.category}</div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="grid grid-cols-1 gap-px bg-[var(--border-default)]">
                  {d.subcategories.map((sc) => (
                    <div key={sc.name} className="bg-[var(--bg-surface)] p-2.5 flex items-center gap-3">
                      <MiniDial value={sc.avg_risk} color={bandColor(sc.avg_risk)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-display truncate">{sc.name}</div>
                        <div className="flex items-center gap-3 label-micro mt-0.5">
                          <span className="sev-critical">● {sc.critical}</span>
                          <span>{sc.signals} sig</span>
                        </div>
                      </div>
                      <div className="font-display text-lg" style={{ color: bandColor(sc.avg_risk) }}>
                        {Math.round(sc.avg_risk * 100)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
         </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
